import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { fmtINR } from "../../lib/constants";
import Paginated from "../../components/Paginated";
import { Eye, X, ArrowUpRight } from "lucide-react";

const paymentType = (o) => {
  const pid = o.razorpay_payment_id || "";
  if (o.payment_method === "counter" || pid.startsWith("counter")) return "Counter Payment";
  if (pid.startsWith("pay_mock") || pid.startsWith("order_mock")) return "Online (Test Mode)";
  if (pid) return "Razorpay Online";
  return o.payment_status === "paid" ? "Online" : "—";
};

export default function OrdersAdmin() {
  const [items, setItems] = useState([]);
  const nav = useNavigate();
  const [bill, setBill] = useState(null);            // order for the bill modal (eye icon)
  const [billProducts, setBillProducts] = useState({});
  const [custView, setCustView] = useState(null);    // 360 view (row click)
  const [custLoading, setCustLoading] = useState(false);

  useEffect(() => { api.get("/api/crm/orders").then((r) => setItems(r.data || [])); }, []);

  // Eye icon → in-depth bill with product details
  const openBill = async (e, o) => {
    e.stopPropagation();
    setBill(o);
    const ids = (o.items || []).map((it) => it.product_id).filter(Boolean);
    const map = {};
    await Promise.all(ids.map(async (id) => {
      try { const { data } = await api.get(`/api/products/${id}`); map[id] = data; } catch {}
    }));
    setBillProducts(map);
  };

  // Row click → everything about that customer (LTV, orders, schemes…)
  const openCustomer = async (o) => {
    const phone = o.customer?.phone || o.address?.phone;
    if (!phone) return;
    setCustLoading(true);
    setCustView({ phone });
    try {
      const { data } = await api.get(`/api/crm/customers/by-phone/${encodeURIComponent(phone)}`);
      setCustView({ phone, ...data });
    } finally { setCustLoading(false); }
  };

  return (
    <div className="p-6 lg:p-8">
      <div className="mb-6"><div className="eyebrow">Sales</div><h1 className="font-display text-4xl text-maroon">Orders</h1>
      <p className="text-xs text-ash mt-1">Click the eye for the bill · click a row for the customer's full profile.</p></div>

      <Paginated items={items} pageSize={15} label="orders" scroller>
        {(pageItems) => (
          <div className="bg-white border border-line">
            <table className="w-full text-sm">
              <thead className="bg-pearl border-b border-line">
                <tr>{["Bill", "Order", "Customer", "Items", "Payment Type", "Payment ID", "Total", "Status", "When"].map((h) => <th key={h} className="text-left p-3 eyebrow text-[10px]">{h}</th>)}</tr>
              </thead>
              <tbody>
                {pageItems.map((o) => {
                  const isCustom = (o.items || []).some((it) => it.is_customised || it.custom || it.customisation);
                  return (
                  <tr key={o.id} onClick={() => openCustomer(o)} className={`border-b border-line/60 cursor-pointer ${isCustom ? "bg-[#efe3d2] hover:bg-[#e7d7c0]" : "hover:bg-pearl"}`} data-testid={`order-${o.id}`} title={isCustom ? "Customisation order" : undefined}>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <button onClick={(e) => openBill(e, o)} className="text-maroon hover:text-gold-dark" title="View bill" data-testid={`bill-eye-${o.id}`}>
                          <Eye size={16}/>
                        </button>
                        {isCustom && (
                          <button onClick={(e) => { e.stopPropagation(); nav("/staff/customisations"); }} className="text-maroon hover:text-gold-dark" title="Open in Customisation Orders" data-testid={`order-custom-link-${o.id}`}>
                            <ArrowUpRight size={16}/>
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="p-3 text-xs">#{o.id.slice(0,8)}{isCustom && <span className="ml-1 text-[9px] uppercase tracking-widest text-[#7a5a2e]">Custom</span>}</td>
                    <td className="p-3">{o.customer?.name} <div className="text-[10px] text-ash">{o.customer?.phone}</div></td>
                    <td className="p-3 text-xs">{(o.items || []).map((it) => it.name).join(", ").slice(0, 40)}{((o.items||[]).map(i=>i.name).join(", ").length > 40) && "…"}</td>
                    <td className="p-3 text-xs">{paymentType(o)}</td>
                    <td className="p-3 text-xs text-ash">{o.razorpay_payment_id || "—"}</td>
                    <td className="p-3 text-maroon font-semibold">{fmtINR(o.total)}</td>
                    <td className="p-3">
                      <span className={`text-[10px] uppercase tracking-widest ${o.payment_status === "paid" ? "text-green-700" : o.payment_status === "failed" ? "text-red-700" : "text-ash"}`}>{o.payment_status}</span>
                    </td>
                    <td className="p-3 text-xs text-ash">{new Date(o.created_at).toLocaleString()}</td>
                  </tr>
                );})}
              </tbody>
            </table>
            {items.length === 0 && <div className="p-12 text-center text-ash text-sm">No orders yet.</div>}
          </div>
        )}
      </Paginated>

      {/* ===== BILL MODAL (eye icon) ===== */}
      {bill && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setBill(null)}>
          <div className="bg-white border border-line w-full max-w-2xl max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="bg-maroon text-pearl p-5 flex items-start justify-between sticky top-0">
              <div>
                <div className="eyebrow text-gold-light">Tax Invoice / Bill</div>
                <div className="font-display text-2xl mt-1">Order #{bill.id.slice(0,8)}</div>
                <div className="text-xs text-pearl/80 mt-1">{new Date(bill.created_at).toLocaleString()}</div>
              </div>
              <button onClick={() => setBill(null)} data-testid="bill-close"><X size={18}/></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="eyebrow text-[10px]">Billed to</div>
                  <div className="font-medium mt-1">{bill.customer?.name}</div>
                  <div className="text-xs text-ash">{bill.customer?.phone} {bill.customer?.email && `· ${bill.customer.email}`}</div>
                  {bill.address && <div className="text-xs text-ash mt-1">{bill.address.line1}, {bill.address.city} {bill.address.pincode}</div>}
                </div>
                <div>
                  <div className="eyebrow text-[10px]">Payment</div>
                  <div className="text-xs mt-1">Type: <b>{paymentType(bill)}</b></div>
                  <div className="text-xs">Payment ID: {bill.razorpay_payment_id || "—"}</div>
                  <div className="text-xs">Status: <span className={bill.payment_status === "paid" ? "text-green-700 font-medium" : ""}>{bill.payment_status}</span></div>
                  {bill.paid_at && <div className="text-xs">Paid at: {new Date(bill.paid_at).toLocaleString()}</div>}
                  {bill.coupon_code && <div className="text-xs">Coupon: {bill.coupon_code}</div>}
                </div>
              </div>

              <div className="border border-line">
                <table className="w-full text-sm">
                  <thead className="bg-pearl border-b border-line">
                    <tr>{["", "Item", "Details", "Qty", "Price"].map((h) => <th key={h} className="text-left p-2 eyebrow text-[10px]">{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {(bill.items || []).map((it, i) => {
                      const p = billProducts[it.product_id] || {};
                      return (
                        <tr key={i} className="border-b border-line/60 align-top">
                          <td className="p-2">
                            {(p.images || [])[0] ? <img src={p.images[0]} alt="" className="w-12 h-12 object-cover bg-pearl border border-line"/> : <div className="w-12 h-12 bg-pearl border border-line"/>}
                          </td>
                          <td className="p-2">
                            <div className="font-medium">{it.name}</div>
                            <div className="text-[10px] text-ash">ID {(it.product_id || "").slice(0,8).toUpperCase()}</div>
                          </td>
                          <td className="p-2 text-[11px] text-ash">
                            {p.metal} {p.purity} {p.weight_grams != null && `· ${p.weight_grams}g`}
                            {p.huid && <> · HUID {p.huid}</>}
                            {(p.making_wastage_percent != null || p.making_charge_percent != null) && p.has_making_wastage !== false && <> · MW {p.making_wastage_percent ?? ((p.making_charge_percent || 0) + (p.wastage_percent || 0))}%</>}
                            {p.stone_charges > 0 && <> · Stones {fmtINR(p.stone_charges)}</>}
                          </td>
                          <td className="p-2">{it.qty}</td>
                          <td className="p-2 text-maroon font-medium">{fmtINR(it.price)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="text-right space-y-1 text-sm">
                {bill.subtotal != null && <div className="text-xs text-ash">Subtotal: {fmtINR(bill.subtotal)}</div>}
                {bill.discount > 0 && <div className="text-xs text-green-700">Discount: −{fmtINR(bill.discount)}</div>}
                {bill.delivery_fee != null && <div className="text-xs text-ash">Delivery: {fmtINR(bill.delivery_fee)}</div>}
                {bill.gst != null && <div className="text-xs text-ash">GST (3%): {fmtINR(bill.gst)}</div>}
                <div className="font-display text-2xl text-maroon">Total: {fmtINR(bill.total)}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===== CUSTOMER 360 DRAWER (row click) ===== */}
      {custView && (
        <div className="fixed inset-0 bg-black/50 z-50 flex justify-end" onClick={() => setCustView(null)}>
          <div className="bg-white w-full max-w-md h-full overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="bg-maroon text-pearl p-5 flex items-start justify-between sticky top-0 z-10">
              <div>
                <div className="eyebrow text-gold-light">Customer 360°</div>
                <div className="font-display text-2xl mt-1">{custView.customer?.name || custView.phone}</div>
                <div className="text-xs text-pearl/80">{custView.phone}</div>
              </div>
              <button onClick={() => setCustView(null)} data-testid="cust-drawer-close"><X size={18}/></button>
            </div>
            {custLoading ? <div className="p-10 text-center text-ash">Loading…</div> : custView.customer ? (
              <div className="p-5 space-y-5">
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div className="border border-line p-3"><div className="text-[10px] text-ash">LTV</div><div className="font-display text-xl text-maroon">{fmtINR(custView.customer.ltv || 0)}</div></div>
                  <div className="border border-line p-3"><div className="text-[10px] text-ash">Orders</div><div className="font-display text-xl text-maroon">{custView.customer.orders_count || 0}</div></div>
                  <div className="border border-line p-3"><div className="text-[10px] text-ash">Loyalty</div><div className="font-display text-xl text-maroon">{custView.customer.loyalty_points || 0}</div></div>
                </div>
                <div className="text-xs text-ash space-y-1">
                  {custView.customer.email && <div>Email: {custView.customer.email}</div>}
                  {custView.customer.birthday && <div>Birthday: {new Date(custView.customer.birthday).toLocaleDateString("en-GB")}</div>}
                  {custView.customer.anniversary && <div>Anniversary: {new Date(custView.customer.anniversary).toLocaleDateString("en-GB")}</div>}
                  {custView.customer.referral_code && <div>Referral code: <b>{custView.customer.referral_code}</b></div>}
                  {custView.customer.last_purchase_at && <div>Last purchase: {new Date(custView.customer.last_purchase_at).toLocaleDateString("en-GB")}</div>}
                </div>
                <section>
                  <div className="eyebrow text-[10px] mb-2">Orders ({custView.orders?.length || 0})</div>
                  <div className="space-y-2">
                    {(custView.orders || []).map((o) => (
                      <div key={o.id} className="border border-line p-2 text-xs">
                        <div className="flex justify-between"><span>#{o.id.slice(0,8)} · {(o.items||[]).map(i=>i.name).join(", ").slice(0,30)}</span><b className="text-maroon">{fmtINR(o.total)}</b></div>
                        <div className="text-ash mt-0.5">{new Date(o.created_at).toLocaleDateString("en-GB")} · {o.payment_status} · {paymentType(o)}</div>
                      </div>
                    ))}
                  </div>
                </section>
                <section>
                  <div className="eyebrow text-[10px] mb-2">Schemes ({custView.schemes?.length || 0})</div>
                  {(custView.schemes || []).map((s) => (
                    <div key={s.id} className="border border-line p-2 text-xs mb-2">
                      {s.scheme_name} · {s.installments_paid}/{s.duration_months} paid · {fmtINR(s.total_paid)}
                    </div>
                  ))}
                </section>
                <section>
                  <div className="eyebrow text-[10px] mb-2">Karigar Jobs ({custView.karigar_jobs?.length || 0})</div>
                  {(custView.karigar_jobs || []).map((j) => (
                    <div key={j.id} className="border border-line p-2 text-xs mb-2">{j.category} · {j.grams}g {j.metal} · <span className="capitalize text-maroon">{j.status}</span></div>
                  ))}
                </section>
              </div>
            ) : (
              <div className="p-10 text-center text-ash text-sm">No CRM record for this phone yet — the order details above are all we have.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
